// estimatePdf.js — Client-facing estimate PDF download for Timeless Tile Pro CRM
// Usage: await downloadEstimatePDF(estimate, { customer, paidToDate })
// Loads html2pdf.js from CDN on first use (no npm install needed).
// If the library can't load, falls back to a print window ("Save as PDF").

const COMPANY = {
  name: "Timeless Tile Pro LLC",
  line: "Virginia Beach, VA · (757) 263-0978 · timelesstilepro.com",
};

const HTML2PDF_SRC = "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";

// ── helpers ──────────────────────────────────────────────────────────────────
const esc = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const nl2br = (s) => esc(s).replace(/\n/g, "<br/>");
const money = (n) =>
  "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const line = (q, p) => (Number(q) || 0) * (Number(p) || 0);
const scopeTotal = (items) => (items || []).reduce((s, i) => s + line(i.qty, i.price), 0);
const matTotal = (items) =>
  (items || []).reduce((s, i) => s + (i.materials || []).reduce((m, x) => m + line(x.qty, x.price), 0), 0);
const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(String(d).length === 10 ? d + "T00:00:00" : d);
  return isNaN(dt) ? String(d) : dt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};
// Same cleanup the CRM uses — removes change-order log text from notes
const stripCONotes = (notes) =>
  (notes || "")
    .replace(/\n*── Change Order CO-\d{3} \(\d{4}-\d{2}-\d{2}\) ──[^─]*/g, "")
    .replace(/\n*— Change Order\nCO-\d{3} \(\d{4}-\d{2}-\d{2}\) — Scope (Edited|Added): \$[\d,]+ → \$[\d,]+.*/g, "")
    .replace(/\n+$/, "")
    .trim();
const safeFile = (s) => String(s || "").replace(/[^a-zA-Z0-9-_ ]/g, "").trim().replace(/\s+/g, "-");

// ── totals (mirrors the CRM's email/preview math) ───────────────────────────
export const estimateTotals = (est, paidToDate = 0) => {
  const computed = scopeTotal(est.items) + matTotal(est.items);
  const base = Number(est.override_total) || computed;
  const surchargeAmt = est.surcharge ? Math.round(base * 0.03) : 0;
  const total = base + surchargeAmt;
  const deposit = Math.round(total * 0.5);
  const paid = Math.min(Number(paidToDate) || 0, total);
  return { computed, base, surchargeAmt, total, deposit, paid, remaining: total - paid };
};

// ── HTML builder ─────────────────────────────────────────────────────────────
const C = { gold: "#c9a96e", dark: "#1a1714", ink: "#2c2820", mid: "#5a5248", light: "#a09880", line: "#ece9e3", green: "#4a7c59", amber: "#8a5a1a" };

const pill = (text, color, bg) =>
  `<span style="display:inline-block;padding:2px 8px;border-radius:5px;font-size:10px;font-weight:700;color:${color};background:${bg};white-space:nowrap">${text}</span>`;

export const buildEstimateHtml = (est, { customer = {}, paidToDate = 0 } = {}) => {
  const itemized = (est.pricingMode || est.pricing_mode) === "itemized";
  const hideMatQty = est.hideMatQty || est.hide_mat_qty;
  const t = estimateTotals(est, paidToDate);
  const notes = stripCONotes(est.notes);
  const issued = fmtDate(est.date);
  const expires = est.date ? fmtDate(new Date(new Date(est.date + "T00:00:00").getTime() + 30 * 864e5)) : "";

  const rows = (est.items || []).map((it, idx) => {
    const right = itemized
      ? `<b>${money(line(it.qty, it.price))}</b>`
      : it.clientSupplied
        ? pill(`Client supplied${it.clientSuppliedNote ? " · " + esc(it.clientSuppliedNote) : ""}`, C.amber, "rgba(201,149,74,0.14)")
        : pill("✓ Included", C.green, "rgba(74,124,89,0.10)");
    const scope = `
      <tr style="page-break-inside:avoid">
        <td style="padding:10px 8px 6px;border-top:${idx ? `1px solid ${C.line}` : "none"};vertical-align:top">
          <div style="font-weight:700;font-size:12.5px;color:${C.ink}">${esc(it.desc)}</div>
          ${it.laborDesc ? `<div style="font-size:10.5px;color:${C.mid};font-style:italic;line-height:1.5;margin-top:3px">${nl2br(it.laborDesc)}</div>` : ""}
          <div style="font-size:10px;color:${C.light};margin-top:3px">${esc(it.qty)} ${esc(it.unit)}${itemized ? ` × ${money(it.price)}` : ""}</div>
        </td>
        <td style="padding:10px 8px 6px;border-top:${idx ? `1px solid ${C.line}` : "none"};text-align:right;vertical-align:top;width:130px">${right}</td>
      </tr>`;
    const mats = (it.materials || []).map((m) => {
      const mRight = itemized
        ? `<span style="font-size:11px;color:${C.mid}">${money(line(m.qty, m.price))}</span>`
        : m.clientSupplied
          ? pill("Client supplied", C.amber, "rgba(201,149,74,0.14)")
          : `<span style="font-size:10px;color:${C.green}">✓ Included</span>`;
      return `
      <tr style="page-break-inside:avoid">
        <td style="padding:3px 8px 3px 26px;vertical-align:top">
          <div style="border-left:2px solid #e8d5a3;padding-left:10px">
            <span style="font-size:8px;font-weight:700;color:${C.gold};background:rgba(201,169,110,0.14);padding:1px 4px;border-radius:3px;margin-right:5px">MTL</span>
            <span style="font-size:11px;color:${C.mid}">${esc(m.desc)}</span>
            ${hideMatQty ? "" : `<span style="font-size:10px;color:${C.light}"> · ${esc(m.qty)} ${esc(m.unit)}${itemized ? ` × ${money(m.price)}` : ""}</span>`}
          </div>
        </td>
        <td style="padding:3px 8px;text-align:right;vertical-align:top">${mRight}</td>
      </tr>`;
    }).join("");
    return scope + mats;
  }).join("");

  const totalLines = [];
  if (itemized) {
    totalLines.push(["Scope subtotal", money(scopeTotal(est.items))]);
    if (matTotal(est.items) > 0) totalLines.push(["Materials subtotal", money(matTotal(est.items))]);
  }
  if (est.override_total && Math.abs(Number(est.override_total) - t.computed) >= 1) {
    totalLines.push(["Project price (adjusted)", money(t.base)]);
  }
  if (t.surchargeAmt) totalLines.push(["3% card processing fee", money(t.surchargeAmt)]);

  const payRows = t.paid > 0
    ? [["Payments received", "− " + money(t.paid), C.green], ["Remaining balance — due on completion", money(t.remaining), C.ink]]
    : [["Deposit (50%) — due to confirm start date", money(t.deposit), C.ink], ["Balance (50%) — due on completion", money(t.total - t.deposit), C.ink]];

  return `
  <div style="width:7.5in;font-family:Helvetica,Arial,sans-serif;color:${C.ink};background:#fff">
    <div style="background:${C.dark};color:#fff;padding:22px 26px;border-radius:10px 10px 0 0;display:flex;justify-content:space-between;align-items:flex-start">
      <div>
        <div style="font-family:Georgia,serif;font-size:22px;font-weight:700">${COMPANY.name}</div>
        <div style="font-size:9.5px;color:rgba(255,255,255,0.5);letter-spacing:.06em;text-transform:uppercase;margin-top:5px">${COMPANY.line}</div>
      </div>
      <div style="text-align:right">
        <div style="font-size:9px;color:rgba(255,255,255,0.45);letter-spacing:.1em;text-transform:uppercase">Estimate</div>
        <div style="font-family:Georgia,serif;font-size:22px;font-weight:700;color:${C.gold}">${esc(est.id)}</div>
        <div style="font-size:10px;color:rgba(255,255,255,0.5);margin-top:2px">Issued ${esc(issued)}</div>
      </div>
    </div>
    <div style="height:3px;background:linear-gradient(90deg,${C.gold},#e8d5a3,#fff)"></div>

    <div style="display:flex;border-bottom:1px solid ${C.line}">
      <div style="flex:1;padding:14px 26px;border-right:1px solid ${C.line}">
        <div style="font-size:9px;font-weight:700;color:${C.light};text-transform:uppercase;letter-spacing:.09em;margin-bottom:4px">Prepared For</div>
        <div style="font-family:Georgia,serif;font-size:16px;font-weight:700">${esc(est.customer)}</div>
        ${customer.address ? `<div style="font-size:11px;color:${C.mid};margin-top:2px">${esc(customer.address)}</div>` : ""}
        ${customer.email ? `<div style="font-size:10.5px;color:${C.light};margin-top:1px">${esc(customer.email)}</div>` : ""}
        ${customer.phone ? `<div style="font-size:10.5px;color:${C.light};margin-top:1px">${esc(customer.phone)}</div>` : ""}
      </div>
      <div style="flex:1;padding:14px 26px">
        <div style="font-size:9px;font-weight:700;color:${C.light};text-transform:uppercase;letter-spacing:.09em;margin-bottom:4px">Project</div>
        <div style="font-family:Georgia,serif;font-size:16px;font-weight:700">${esc(est.project)}</div>
        ${expires ? `<div style="font-size:10.5px;color:${C.light};margin-top:2px">Valid until ${esc(expires)}</div>` : ""}
      </div>
    </div>

    <div style="padding:16px 18px 0">
      <table style="width:100%;border-collapse:collapse">
        <thead>
          <tr>
            <th style="text-align:left;font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:${C.light};padding:0 8px 8px;border-bottom:2px solid ${C.dark}">Scope of Work</th>
            <th style="text-align:right;font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:${C.light};padding:0 8px 8px;border-bottom:2px solid ${C.dark}">${itemized ? "Amount" : "Status"}</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>

    <div style="display:flex;justify-content:flex-end;padding:14px 26px 6px;page-break-inside:avoid">
      <div style="width:290px">
        ${totalLines.map(([l, v]) => `<div style="display:flex;justify-content:space-between;font-size:11.5px;color:${C.mid};padding:5px 0;border-top:1px solid ${C.line}"><span>${l}</span><span>${v}</span></div>`).join("")}
        <div style="display:flex;justify-content:space-between;align-items:center;background:${C.dark};border-radius:8px;padding:11px 16px;margin-top:6px">
          <span style="font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:rgba(255,255,255,0.6)">Project Total</span>
          <span style="font-family:Georgia,serif;font-size:21px;font-weight:700;color:${C.gold}">${money(t.total)}</span>
        </div>
      </div>
    </div>

    <div style="padding:10px 26px;page-break-inside:avoid">
      <div style="border:1px solid rgba(201,169,110,0.35);border-radius:8px;overflow:hidden">
        <div style="background:rgba(201,169,110,0.10);padding:7px 14px;font-size:9px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:${C.light}">Payment Terms</div>
        ${payRows.map(([l, v, col]) => `<div style="display:flex;justify-content:space-between;padding:8px 14px;border-top:1px solid ${C.line};font-size:11.5px"><span style="color:${C.mid}">${l}</span><b style="color:${col}">${v}</b></div>`).join("")}
        <div style="display:flex;justify-content:space-between;padding:8px 14px;border-top:1px solid ${C.line};font-size:11px"><span style="color:${C.mid}">Accepted methods</span><span>Card · Bank Transfer · Check</span></div>
      </div>
      ${t.surchargeAmt ? `<div style="font-size:10px;color:${C.mid};margin-top:6px">A 3% fee is included for card payments. Pay by check or bank transfer to waive it.</div>` : ""}
    </div>

    ${notes ? `
    <div style="padding:6px 26px;page-break-inside:avoid">
      <div style="border-left:3px solid ${C.gold};background:#f7f5f1;border-radius:0 8px 8px 0;padding:10px 14px">
        <div style="font-size:9px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:${C.light};margin-bottom:4px">Project Notes</div>
        <div style="font-size:11px;color:${C.mid};line-height:1.6">${nl2br(notes)}</div>
      </div>
    </div>` : ""}

    <div style="padding:16px 26px 8px;page-break-inside:avoid">
      <div style="font-size:10px;color:${C.mid};line-height:1.6;margin-bottom:22px">
        By signing below, you authorize ${COMPANY.name} to proceed with the project as described and agree to the payment terms above.
      </div>
      <div style="display:flex;gap:28px">
        ${["Client Signature", "Printed Name", "Date"].map((l) => `<div style="flex:1"><div style="border-bottom:1.5px solid ${C.ink};height:24px"></div><div style="font-size:9px;color:${C.light};margin-top:4px">${l}</div></div>`).join("")}
      </div>
    </div>

    <div style="margin-top:14px;background:#242018;padding:12px 26px;border-radius:0 0 10px 10px;display:flex;justify-content:space-between;font-size:10px">
      <span style="font-family:Georgia,serif;font-style:italic;color:rgba(255,255,255,0.45)">${COMPANY.name} — Thank you for your business.</span>
      ${expires ? `<span style="color:${C.gold};font-weight:700">Valid until ${esc(expires)}</span>` : ""}
    </div>
  </div>`;
};

// ── PDF library loader ───────────────────────────────────────────────────────
let libPromise = null;
const loadHtml2Pdf = () => {
  if (window.html2pdf) return Promise.resolve(window.html2pdf);
  if (!libPromise) {
    libPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = HTML2PDF_SRC;
      s.async = true;
      s.onload = () => (window.html2pdf ? resolve(window.html2pdf) : reject(new Error("PDF library failed to initialize")));
      s.onerror = () => { libPromise = null; reject(new Error("Could not load PDF library")); };
      document.head.appendChild(s);
    });
  }
  return libPromise;
};

// Fallback: open a print window so the user can "Save as PDF"
const printFallback = (html, title) => {
  const w = window.open("", "_blank");
  if (!w) throw new Error("Pop-up blocked — allow pop-ups to download the estimate");
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${esc(title)}</title>
    <style>@page{size:letter;margin:0.4in}body{margin:0;-webkit-print-color-adjust:exact;print-color-adjust:exact}</style>
    </head><body>${html}</body></html>`);
  w.document.close();
  w.onload = () => { w.focus(); w.print(); };
};

// ── Main export ──────────────────────────────────────────────────────────────
export const downloadEstimatePDF = async (est, { customer = {}, paidToDate = 0 } = {}) => {
  if (!est) throw new Error("No estimate selected");
  const html = buildEstimateHtml(est, { customer: customer || {}, paidToDate });
  const filename = `Estimate-${safeFile(est.id)}-${safeFile(est.customer)}.pdf`;

  let html2pdf;
  try {
    html2pdf = await loadHtml2Pdf();
  } catch (e) {
    printFallback(html, filename.replace(/\.pdf$/, ""));
    return { method: "print" };
  }

  const el = document.createElement("div");
  el.innerHTML = html;

  await html2pdf()
    .set({
      margin: [0.4, 0.5, 0.45, 0.5],
      filename,
      image: { type: "jpeg", quality: 0.96 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
      pagebreak: { mode: ["css", "legacy"], avoid: ["tr"] },
    })
    .from(el.firstElementChild)
    .save();

  return { method: "pdf", filename };
};

export default downloadEstimatePDF;
